/**
 * <analytics-opt-out> — the Art. 21 objection the privacy policy's §3
 * promises, standing at the end of the clause that describes what it turns
 * off. One fact and one gesture: measurement is on or off for this browser,
 * and the switch changes which.
 *
 * Zero I/O. The gesture leaves as a request on `analyticsOptOutRequest$` and
 * whichever reconciler is mounted answers it against the visitor's own buyer
 * document — the objection is recorded there and nowhere on the device,
 * which is what lets the policy promise it while promising no new storage in
 * the same breath.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { html } from "lit";
import { state } from "lit/decorators.js";
import { filter, firstValueFrom, takeUntil } from "rxjs";
import { LocalizeMixin } from "../i18n/localize.ts";
import {
	analyticsOptOut,
	analyticsOptOutRequest$,
	analyticsOptOutResult$,
} from "./analytics.state.ts";

@schmancyElement("analytics-opt-out")
export class AnalyticsOptOutView extends LocalizeMixin(SchmancyElement) {
	@state() private pending = false;
	@state() private failedValue: boolean | null = null;

	private async choose(value: boolean): Promise<void> {
		if (this.pending || analyticsOptOut.value === null) return;
		const desired = this.failedValue ?? value;
		if (this.failedValue === null && desired === analyticsOptOut.value) return;
		this.pending = true;
		try {
			const requestId = crypto.randomUUID();
			const answer = firstValueFrom(
				analyticsOptOutResult$.pipe(
					filter((result) => result.requestId === requestId),
					takeUntil(this.disconnecting),
				),
			);
			analyticsOptOutRequest$.next({ requestId, value: desired });
			if (!(await answer).ok) this.failedValue = desired;
			else this.failedValue = null;
		} catch {
			this.failedValue = desired;
		} finally {
			this.pending = false;
		}
	}
	override render() {
		const label = this.t("legal.privacy.s3.optOut.label");
		return html`
			<div class="flex flex-col gap-2">
				<div class="flex items-center justify-between gap-3">
					<schmancy-typography type="body" token="md"><span style="display:grid"><span style=${this.failedValue !== null ? "grid-area:1/1;visibility:hidden" : "grid-area:1/1"} aria-hidden=${this.failedValue !== null ? "true" : "false"}>${label}</span><span style=${this.failedValue !== null ? "grid-area:1/1" : "grid-area:1/1;visibility:hidden"} aria-hidden=${this.failedValue !== null ? "false" : "true"}>${this.t("legal.privacy.s3.optOut.saveFailed")}</span></span></schmancy-typography>
					<!-- The label prop is ARIA-only (switch.md), so the visible
					     wording is the typography beside the control and the control
					     carries the same words as its accessible name. The equality
					     guard is deliberate: the switch re-emits change on the checked
					     binding this render itself sets, so only a value differing from
					     the one on screen is a gesture. -->
					<schmancy-switch
						label=${label}
						?checked=${analyticsOptOut.value === true}
						.busy=${this.pending || analyticsOptOut.value === null}
						?error=${this.failedValue !== null}
						@change=${(event: CustomEvent<{ value: boolean }>) => this.choose(event.detail.value)}
					></schmancy-switch>
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"analytics-opt-out": AnalyticsOptOutView;
	}
}
